import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  ColDef,
  ColGroupDef,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgBooleanCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgDateRangeFilterComponent } from '@sensika/ag-grid-kit';
import { DataGridHostBase } from 'src/app/ag-grid/data-grid-host-base';
import {
  buildAuditTrailColumnGroup,
  buildOwnerColumnDef,
} from 'src/app/ag-grid/utils/audit-trail-columns.utils';
import { buildNomenclatureColumnDef } from 'src/app/ag-grid/utils/nomenclature-column.utils';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { NomObjectLifecycleStatus } from '../../constants';
import { ObjectEditorService } from '../../services/object-editor.service';
import { ToastService } from '@services/toast.service';
import { redirectToObject } from 'src/app/shared/utils/object-utils';

@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  standalone: false,
})
export class AlertsComponent extends DataGridHostBase {
  private readonly nomenclatureService = inject(NomenclatureService);
  private readonly userApi = inject(UserApiService);
  private readonly editService = inject(ObjectEditorService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  readonly viewId = ViewId.Alerts;
  readonly title = this.translateService.instant('alerts.title.alerts');
  readonly icon: IconProp = ['fas', 'bell'];

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.userApi.getUserAlertsAgGrid(request);

  readonly columnDefs: (ColDef | ColGroupDef)[] = this.buildColumnDefs();

  private buildColumnDefs(): (ColDef | ColGroupDef)[] {
    return [
      {
        headerName: this.translateService.instant('grid.column.alert-company'),
        initialHide: true,
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'company.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 180,
            initialHide: true,
          },
          {
            headerName: this.translateService.instant('grid.column.is-enabled'),
            field: 'company.is_enabled',
            minWidth: 100,
            maxWidth: 120,
            initialHide: true,
            filterParams: {
              values: ['true', 'false'],
            },
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.company.is_enabled',
            },
          },
        ],
      },
      {
        headerName: this.translateService.instant('grid.column.alert-parent'),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.workspace'),
            field: 'workspace.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 180,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'workspace',
              icon: ['fas', 'folder'],
              // Only refs the dispatcher can open are links.
              clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
              onClick: (ref) => this.editService.openDataObjectRef(ref),
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            ...buildNomenclatureColumnDef(
              this.translateService,
              this.nomenclatureService,
              {
                headerNameKey: 'grid.column.alert-context-object-type',
                fieldKey: 'context_object.type_id',
                fieldValue: 'context_object.type_name',
                nomenclatureName: 'object_type',
                otherFilterParams: {
                  defaultToNothingSelected: true,
                },
              },
            ),
            initialHide: true,
          },
          {
            headerName: this.translateService.instant('grid.column.feed'),
            field: 'context_object.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 250,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              object: 'context_object',
              // Only refs the dispatcher can open are links.
              clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
              onClick: (ref) => this.editService.openDataObjectRef(ref),
            },
            cellStyle: { overflow: 'visible' },
          },
        ],
      },
      {
        headerName: this.translateService.instant('grid.column.alert-details'),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'title',
            filter: 'agTextColumnFilter',
            minWidth: 200,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'title',
              objectId: 'id',
              objectClassId: 'object_class_id',
              objectTypeId: 'type_id',
              lifecycleStatus: 'lifecycle_status.id',
              onClick: (data) => {
                if (
                  data?.lifecycle_status.id ===
                    NomObjectLifecycleStatus.ACTIVE &&
                  data.can_view &&
                  data.can_edit
                ) {
                  redirectToObject(data, this.router, true);
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'alerts.error.you-do-not-have-permissions-or-alert-not-active',
                    ),
                    this.translateService.instant('alerts.error.not-available'),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            headerName: this.translateService.instant(
              'grid.column.description',
            ),
            field: 'description',
            filter: 'agTextColumnFilter',
            initialHide: true,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.alert-trigger-type',
            ),
            field: 'trigger_type.title',
            filter: 'agTextColumnFilter',
            minWidth: 160,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.attached-dimension',
            ),
            field: 'dimension.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 150,
          },
          buildNomenclatureColumnDef(
            this.translateService,
            this.nomenclatureService,
            {
              headerNameKey: 'grid.column.alert-execution-status',
              fieldKey: 'execution_status.id',
              fieldValue: 'execution_status.title',
              nomenclatureName: 'alert_execution_status',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            },
          ),
          {
            headerName: this.translateService.instant(
              'grid.column.time-last-triggered',
            ),
            field: 'time_last_trigger.absolute',
            filter: AgDateRangeFilterComponent,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.recipients-count',
            ),
            field: 'recipients_count',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            sortable: true,
            maxWidth: 160,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.alert-are-you-recipient',
            ),
            field: 'is_user_subscribed',
            sortable: false,
            floatingFilter: false,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            maxWidth: 200,
            cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
          },
          {
            headerName: this.translateService.instant(
              'grid.column.alert-user-status',
            ),
            field: 'user_status.title',
            sortable: false,
            floatingFilter: false,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,

            maxWidth: 150,
          },
          buildNomenclatureColumnDef(
            this.translateService,
            this.nomenclatureService,
            {
              headerNameKey: 'grid.column.lifecycle-status',
              fieldKey: 'lifecycle_status.id',
              fieldValue: 'lifecycle_status.title',
              nomenclatureName: 'object_lifecycle_status',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            },
          ),
        ],
      },
      buildOwnerColumnDef(this.translateService, this.openUser),
      buildAuditTrailColumnGroup(this.translateService, {
        area: 'analytics',
        onUserClick: this.openUser,
      }),
    ];
  }
}
